/**
 * Root Application Component
 * 
 * Main app container that manages:
 * - Routing configuration
 * - Theme provider
 * - Global state
 * 
 * Features:
 * - React Router setup
 * - Dark mode support
 * - Lazy loading
 * - Global error boundary (catches unhandled render errors app-wide)
 * 
 * @component
 * @returns {React.ReactElement} The root application component
 */

import React, { useState, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AnimatedRoutes from './components/layout/AnimatedRoutes';
import { ThemeProvider } from './context/ThemeContext';
import { LoadingProvider } from './context/LoadingContext';
import GlobalLoader from './components/layout/GlobalLoader';
import ErrorBoundary from './components/common/ErrorBoundary';
import ErrorFallback from './components/common/ErrorFallback';
import './App.css';

// Global-level error fallback — uses plain <a> and reload instead of React Router
// because the Router itself may have crashed at this level
const GlobalErrorFallback = (props) => <ErrorFallback {...props} level="global" />;

// Loading fallback for the outer Suspense boundary
// Shown while the initial page chunk is downloading
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
  </div>
);

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <HelmetProvider>
    <ThemeProvider>
      <LoadingProvider>
        {/* Global error boundary — catches any unhandled error including Router crashes */}
        <ErrorBoundary fallback={GlobalErrorFallback}>
        <Router>
          <div className="flex flex-col min-h-screen bg-light-background dark:bg-dark-background text-light-onBackground dark:text-dark-onBackground transition-colors duration-200">
            <GlobalLoader />
            <Header
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
            />
            {/* Outer Suspense catches lazy-load boundaries for all pages. */}
            {/* AnimatedRoutes handles AnimatePresence + route definitions. */}
            <Suspense fallback={<LoadingSpinner />}>
              <main className="flex-grow container mx-auto px-4 pt-32 pb-16 w-full max-w-[1280px]">
                <AnimatedRoutes setCurrentPage={setCurrentPage} />
              </main>
            </Suspense>
            <Footer />
          </div>
        </Router>
        </ErrorBoundary>
      </LoadingProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;